import React, { useState, useEffect } from 'react';
import { isManagerRole, isAdminLike } from '../utils/roleMapping';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    Typography,
    Box,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    Avatar,
    alpha,
    Alert,
    CircularProgress,
} from '@mui/material';
import {
    Person,
    CompareArrows,
    Close,
    Info,
} from '@mui/icons-material';
import { Lead, User } from '../types';
import { useAuth } from '../contexts/AuthContext';
import { fetchSalesUsers, fetchSalesUsersByManager, getAllActiveAgentUsers, AgentUserRS } from '../utils/api';

// Interface para representar os dados retornados pelo backend
interface BackendUserSession {
    id: string;
    name: string;
    username: string;
    email: string;
    phone: string;
    role: string;
    shouldResetPassword: boolean;
    isAdmin: boolean;
    shouldRedirectWelcome: boolean;
    isEmailVerified: boolean;
    active: boolean;
    creationTime: string;
    lastUpdateTime: string;
    isDeleted: boolean;
    organizationId: string;
    managerUserId?: string;
}

interface TransferLeadModalProps {
    open: boolean;
    lead: Lead | null;
    onClose: () => void;
    onTransfer: (leadId: string, newUserId: string) => void;
    isMobile?: boolean;
}

const TransferLeadModal: React.FC<TransferLeadModalProps> = ({
    open,
    lead,
    onClose,
    onTransfer,
    isMobile = false,
}) => {
    const { user } = useAuth();
    const [selectedUserId, setSelectedUserId] = useState<string>('');
    const [users, setUsers] = useState<BackendUserSession[]>([]);
    const [agentUsers, setAgentUsers] = useState<AgentUserRS[]>([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);

    useEffect(() => {
        if (open) {
            loadUsers();
        }
    }, [open]);

    const loadUsers = async () => {
        setLoading(true);
        setError(null);
        try {
            const salesUsers = user?.role === 'gerente'
                ? await fetchSalesUsersByManager()
                : await fetchSalesUsers();

            console.log('Dados recebidos do backend:', salesUsers);
            console.log('Tipo dos dados:', typeof salesUsers, Array.isArray(salesUsers));
            console.log('Quantidade de usuários:', salesUsers?.length || 0);

            setUsers(salesUsers as BackendUserSession[]);
            
            // Also load agent users for transfer
            const agentUsersData = await getAllActiveAgentUsers();
            setAgentUsers(agentUsersData);
        } catch (err) {
            console.error('Erro ao carregar usuários:', err);
            setError('Erro ao carregar usuários');
        } finally {
            setLoading(false);
        }
    };

    const handleTransfer = () => {
        if (lead && selectedUserId) {
            onTransfer(lead._id.$oid, selectedUserId);
            setSelectedUserId('');
            onClose();
        }
    };

    if (!lead) return null;

    // Obter usuário atualmente atribuído ao lead
    const currentAssignedUser = users.find((u: BackendUserSession) => u.id === lead.assignedTo);

    // Filter agent users by the lead's agent and apply role-based permissions
    const availableAgentUsers = agentUsers.filter(au => 
        au.isActive &&
        // Filter by lead's agent
        au.agentId === lead.agentId &&
        // Exclude current assigned user
        au.userId !== lead.assignedTo &&
        // Apply role-based permissions
        (isAdminLike(user?.role || '') ||
          (isManagerRole(user?.role || '') && users.find(u => u.id === au.userId)?.managerUserId === user?.id))
    );

    // Only use AgentUsers for transfer - no fallback to traditional users
    const availableUsers = availableAgentUsers.map(au => ({
        id: au.userId,
        name: au.userName || au.userId,
        username: au.userUsername || '',
        email: au.userUsername || '',
        phone: au.userPhone || '',
        role: 'Sales',
        shouldResetPassword: false,
        isAdmin: false,
        shouldRedirectWelcome: false,
        isEmailVerified: true,
        active: true,
        creationTime: '',
        lastUpdateTime: '',
        isDeleted: false,
        organizationId: '',
        managerUserId: users.find(u => u.id === au.userId)?.managerUserId
    }));

    console.log('Agent Users disponíveis para transferência:', availableAgentUsers);
    console.log('Usuários disponíveis para transferência:', availableUsers);
    console.log('Lead Agent ID:', lead.agentId);

    return (
        <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
            <DialogTitle sx={{
                display: 'flex',
                alignItems: 'center',
                background: 'linear-gradient(135deg, #61CE70 0%, #61CE70 100%)',
                color: 'white'
            }}>
                <CompareArrows sx={{ mr: 1 }} />
                <Typography variant="h6" component="div" sx={{ flexGrow: 1 }}>
                    Transferir Lead
                </Typography>
                <Button
                    onClick={onClose}
                    sx={{
                        color: 'white',
                        minWidth: 'auto',
                        p: 1,
                    }}
                >
                    <Close />
                </Button>
            </DialogTitle>

            <DialogContent sx={{ p: 3, mt: 3 }}>
                <Box sx={{ mb: 3 }}>
                    {/* Informações do lead */}
                    <Box sx={{
                        p: 2,
                        backgroundColor: alpha('#61CE70', 0.1),
                        borderRadius: 2,
                        border: `1px solid ${alpha('#61CE70', 0.2)}`,
                        mb: 3,
                    }}>
                        <Typography variant="h6" sx={{ fontWeight: 'bold', mb: 1 }}>
                            {lead.name}
                        </Typography>
                        <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
                            📱 {lead.phoneNumber}
                        </Typography>
                        <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                            📧 {lead.email}
                        </Typography>

                        {/* Vendedor atual */}
                        {currentAssignedUser && (
                            <Box sx={{
                                display: 'flex',
                                alignItems: 'center',
                                mt: 1,
                                p: 1,
                                backgroundColor: alpha('#f44336', 0.1),
                                borderRadius: 1,
                                border: `1px solid ${alpha('#f44336', 0.2)}`,
                            }}>
                                <Info sx={{ fontSize: 16, mr: 1, color: '#f44336' }} />
                                <Typography variant="body2" sx={{ fontSize: '0.875rem' }}>
                                    <strong>Vendedor atual:</strong> {currentAssignedUser.name}
                                </Typography>
                            </Box>
                        )}
                    </Box>

                    {/* Erro de carregamento */}
                    {error && (
                        <Alert severity="error" sx={{ mb: 2 }}>
                            {error}
                        </Alert>
                    )}

                    {/* Loading ou seleção do novo vendedor */}
                    {loading ? (
                        <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                            <CircularProgress />
                        </Box>
                    ) : (
                        <>
                            {/* Seleção do novo vendedor */}
                            <FormControl fullWidth size="small">
                                <InputLabel>Selecionar Novo Vendedor</InputLabel>
                                <Select
                                    value={selectedUserId}
                                    label="Selecionar Novo Vendedor"
                                    onChange={(e) => setSelectedUserId(e.target.value)}
                                >
                                    {availableUsers.map((transferUser: BackendUserSession) => (
                                        <MenuItem key={transferUser.id} value={transferUser.id}>
                                            <Box sx={{ display: 'flex', alignItems: 'center', width: '100%' }}>
                                                <Avatar
                                                    sx={{
                                                        width: 32,
                                                        height: 32,
                                                        mr: 2,
                                                        backgroundColor: '#61CE70',
                                                    }}
                                                >
                                                    <Person fontSize="small" />
                                                </Avatar>
                                                <Box>
                                                    <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
                                                        {transferUser.name}
                                                    </Typography>
                                                    <Typography variant="caption" color="text.secondary">
                                                        {transferUser.email || transferUser.username}
                                                    </Typography>
                                                </Box>
                                            </Box>
                                        </MenuItem>
                                    ))}
                                </Select>
                            </FormControl>

                            {availableUsers.length === 0 && !loading && (
                                <Alert severity="info" sx={{ mt: 2 }}>
                                    Nenhum vendedor disponível para transferência
                                </Alert>
                            )}

                            {/* Aviso sobre a transferência */}
                            <Alert severity="warning" sx={{ mt: 2 }}>
                                <Typography variant="body2">
                                    <strong>Atenção:</strong> Esta ação transferirá o lead para outro vendedor.
                                    O vendedor atual perderá o acesso a este lead.
                                </Typography>
                            </Alert>
                        </>
                    )}
                </Box>
            </DialogContent>

            <DialogActions sx={{ p: 3, pt: 0 }}>
                <Button
                    onClick={onClose}
                    color="inherit"
                    disabled={loading}
                    sx={{ mr: 1 }}
                >
                    Cancelar
                </Button>
                <Button
                    onClick={handleTransfer}
                    variant="contained"
                    disabled={!selectedUserId || loading}
                    sx={{
                        backgroundColor: '#61CE70',
                        '&:hover': {
                            backgroundColor: '#61CE70',
                        },
                    }}
                >
                    {loading ? (
                        <CircularProgress size={20} sx={{ mr: 1 }} />
                    ) : (
                        <CompareArrows sx={{ mr: 1 }} />
                    )}
                    Transferir Lead
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export default TransferLeadModal;
